import React, { memo, useState, useCallback, useEffect, useRef } from "react";
import { Tag, Select, Button, Input, Badge, Spin } from "antd";
import {
  EnvironmentOutlined,
  ReloadOutlined,
  SearchOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  AimOutlined,
} from "@ant-design/icons";
import { Map as LeafletMap, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getApi } from "utils/apiCall";
import moment from "moment";

const { Option } = Select;

const BRAND = "#2f6fe0";
const GREEN = "#1A8F5C";
const ORANGE = "#B4690E";
const DEFAULT_CENTER = [-37.8136, 144.9631];
const DEFAULT_ZOOM = 12;

// ─── Custom Marker Icons ─────────────────────────────────────────────────────

const createMarkerIcon = (color, isSelected) =>
  new L.DivIcon({
    className: "",
    html: `<div style="
      width:${isSelected ? 32 : 26}px; height:${isSelected ? 32 : 26}px; border-radius:50%;
      background:${color}; border:3px solid #fff;
      box-shadow:${isSelected ? `0 0 0 3px ${color}50, ` : ""}0 2px 8px rgba(0,0,0,0.3);
      transition: all 0.2s ease;
    "></div>`,
    iconSize: [isSelected ? 32 : 26, isSelected ? 32 : 26],
    iconAnchor: [isSelected ? 16 : 13, isSelected ? 16 : 13],
    popupAnchor: [0, isSelected ? -18 : -14],
  });

const onlineIcon = createMarkerIcon(GREEN, false);
const offlineIcon = createMarkerIcon(ORANGE, false);
const onlineSelectedIcon = createMarkerIcon(GREEN, true);
const offlineSelectedIcon = createMarkerIcon(ORANGE, true);

// ─── Staff List Item ─────────────────────────────────────────────────────────

const StaffListItem = ({ staff, isSelected, onClick }) => {
  const isOnline = staff.status === "online";
  const statusColor = isOnline ? GREEN : ORANGE;
  return (
    <div
      onClick={() => onClick(staff)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px", cursor: "pointer",
        background: isSelected ? "#f0f5fb" : "#fff",
        borderBottom: "1px solid #f5f5f5",
        transition: "all 0.15s ease",
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: "50%",
        background: "#f0f5fb", border: `2px solid ${statusColor}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 700, color: BRAND, flexShrink: 0,
      }}>
        {(staff.staff_name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#101828", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {staff.staff_name || "Unknown"}
        </div>
        <div style={{ fontSize: 11, color: "#5B6472", marginTop: 2 }}>
          {staff.facility_name || "-"}
        </div>
      </div>
      <div style={{ width: 10, height: 10, borderRadius: "50%", background: statusColor, flexShrink: 0 }} />
    </div>
  );
};

// ─── Staff Detail Card (overlays on right side of map) ───────────────────────

const StaffDetailCard = ({ staff, onClose }) => {
  if (!staff) return null;
  const isOnline = staff.status === "online";

  return (
    <div style={{
      position: "absolute", top: 16, right: 16, width: 300, zIndex: 1000,
      background: "#fff", borderRadius: 12,
      boxShadow: "0 6px 24px rgba(0,0,0,0.12)", overflow: "hidden",
    }}>
      {/* Header with name */}
      <div style={{ padding: "16px 18px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: "50%",
          background: "#f0f5fb", border: `2px solid ${isOnline ? GREEN : ORANGE}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 15, fontWeight: 700, color: BRAND, flexShrink: 0,
        }}>
          {(staff.staff_name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#101828" }}>
            {staff.staff_name || "Unknown"}
          </div>
          <div style={{ fontSize: 12, color: "#5B6472", marginTop: 2 }}>
            {staff.facility_name || "-"}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <CloseOutlined onClick={onClose} style={{ color: "#999", cursor: "pointer", fontSize: 14 }} />
          <Tag color={isOnline ? "green" : "orange"} style={{ margin: 0, fontSize: 11 }}>
            {isOnline ? "On Site" : "Offline"}
          </Tag>
        </div>
      </div>
      {/* Details */}
      <div style={{ padding: "14px 18px" }}>
        <DetailRow
          icon={<AimOutlined style={{ color: BRAND }} />}
          label="Current Location"
          value={staff.facility_name || "-"}
        />
        <DetailRow
          icon={<ClockCircleOutlined style={{ color: BRAND }} />}
          label="Checked In"
          value={staff.login_at ? moment(staff.login_at).format("hh:mm A") : "-"}
        />
        <DetailRow
          icon={<ClockCircleOutlined style={{ color: GREEN }} />}
          label="Time On Site"
          value={staff.login_at ? (() => {
            const diff = moment().diff(moment(staff.login_at), "minutes");
            const h = Math.floor(diff / 60);
            const m = diff % 60;
            return `${h}h ${m}m`;
          })() : "-"}
        />
        <DetailRow
          icon={<ClockCircleOutlined style={{ color: "#5B6472" }} />}
          label="Last Updated"
          value={staff.last_updated ? moment(staff.last_updated).format("DD-MM-YYYY, hh:mm A") : "-"}
        />
      </div>
    </div>
  );
};

const DetailRow = ({ icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid #f8f8f8" }}>
    <div style={{ marginTop: 2, flexShrink: 0 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 11, color: "#5B6472" }}>{label}</div>
      <div style={{ fontSize: 13, color: "#101828", fontWeight: 500 }}>{value}</div>
    </div>
  </div>
);

// ─── Main Tracking Page ──────────────────────────────────────────────────────

const StaffTracking = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [filterFacility, setFilterFacility] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const mapRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterFacility
        ? `/facility/live-staff-locations?facility_id=${filterFacility}`
        : "/facility/live-staff-locations";
      const { error, result } = await getApi(url);
      if (!error && result) {
        const records = result.data || result.result || [];
        const list = Array.isArray(records) ? records : [];
        setData(list);
        const facilityMap = new Map();
        list.forEach((r) => {
          if (r.facility_id && !facilityMap.has(r.facility_id)) {
            facilityMap.set(r.facility_id, { facility_id: r.facility_id, facility_name: r.facility_name });
          }
        });
        if (!filterFacility) setFacilities(Array.from(facilityMap.values()));
        if (list.length > 0 && list[0].latitude && list[0].longitude && !mapRef.current) {
          setMapCenter([Number(list[0].latitude), Number(list[0].longitude)]);
        }
      }
    } catch (err) {
      console.error("Error fetching live locations:", err);
    } finally {
      setLoading(false);
    }
  }, [filterFacility]);

  useEffect(() => {
    fetchLocations();
    intervalRef.current = setInterval(fetchLocations, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchLocations]);

  const filteredData = data.filter((s) =>
    (s.staff_name || "").toLowerCase().includes(searchText.toLowerCase())
  );
  const onlineCount = data.filter((d) => d.status === "online").length;
  const offlineCount = data.filter((d) => d.status === "offline").length;

  const handleStaffClick = (staff) => {
    setSelectedStaff(staff);
    if (staff.latitude && staff.longitude) {
      const leafletMap = mapRef.current && mapRef.current.leafletElement;
      if (leafletMap) {
        leafletMap.setView([Number(staff.latitude), Number(staff.longitude)], 15, { animate: true });
      }
    }
  };

  return (
    <div style={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* Top Header */}
      <div style={{ padding: "16px 24px", background: "#fff", borderBottom: "1px solid #edf2f9" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: "#101828" }}>Live GPS Workforce Tracking</span>
              <span style={{ fontSize: 16 }}>📡</span>
            </div>
            <div style={{ fontSize: 13, color: "#5B6472", marginTop: 2 }}>
              Real-time visibility of your staff in the field.
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Select
              placeholder="All Staff"
              allowClear
              style={{ width: 160 }}
              value={filterFacility}
              onChange={(val) => { setFilterFacility(val); setSelectedStaff(null); }}
            >
              {facilities.map((f) => (
                <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={fetchLocations} loading={loading} />
            <Badge dot color={GREEN} offset={[-2, 2]}>
              <Tag color="green" style={{ margin: 0, fontWeight: 600 }}>● Live</Tag>
            </Badge>
          </div>
        </div>
      </div>

      {/* Map Area (full width) with overlaying panels */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {/* The Map — full width & height */}
        <LeafletMap
          center={mapCenter}
          zoom={DEFAULT_ZOOM}
          style={{ height: "100%", width: "100%", zIndex: 1 }}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {data.filter((s) => s.latitude && s.longitude).map((staff) => {
            const isSelected = selectedStaff && selectedStaff.staff_id === staff.staff_id;
            const isOnline = staff.status === "online";
            return (
              <Marker
                key={staff.staff_id}
                position={[Number(staff.latitude), Number(staff.longitude)]}
                icon={isSelected
                  ? (isOnline ? onlineSelectedIcon : offlineSelectedIcon)
                  : (isOnline ? onlineIcon : offlineIcon)}
                onClick={() => handleStaffClick(staff)}
              />
            );
          })}
        </LeafletMap>

        {/* Left overlay: Staff List Panel */}
        <div style={{
          position: "absolute", top: 16, left: 16, bottom: 16, width: 280,
          background: "#fff", borderRadius: 12, zIndex: 1000,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          {/* Panel Header */}
          <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#101828" }}>
                Live Workforce ({data.length})
              </span>
              <div style={{ display: "flex", gap: 6, fontSize: 10 }}>
                <span style={{ color: GREEN, fontWeight: 600 }}>● {onlineCount}</span>
                <span style={{ color: ORANGE, fontWeight: 600 }}>● {offlineCount}</span>
              </div>
            </div>
            <Input
              placeholder="Search staff..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </div>
          {/* Staff List */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading && !data.length ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spin size="small" /></div>
            ) : filteredData.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "#5B6472", fontSize: 12 }}>No staff found</div>
            ) : (
              filteredData.map((staff) => (
                <StaffListItem
                  key={staff.staff_id}
                  staff={staff}
                  isSelected={selectedStaff?.staff_id === staff.staff_id}
                  onClick={handleStaffClick}
                />
              ))
            )}
          </div>
        </div>

        {/* Right overlay: Staff Detail Card */}
        <StaffDetailCard staff={selectedStaff} onClose={() => setSelectedStaff(null)} />
      </div>
    </div>
  );
};

export default memo(StaffTracking);
